import { useState } from "react";
import { format } from "date-fns";
import { formatCardDate } from "@/design/format";
import { FileCheck2, ShieldCheck, Download, FileText } from "lucide-react";
import {
  DataTable,
  type Column,
  Pill,
  Button,
  Card,
  CardBody,
  EmptyState,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  NarrativeCard,
} from "@/design/components";
import { useSeed, useDeclarations, useMoney, usePeopleIndex } from "@/domain/hooks";
import { useFileDeclaration } from "@/domain/useMutations";
import type { Declaration } from "@/domain/types";
import { toast } from "sonner";

const PACK_RULES = [
  { rule: "Inbound-funds declaration", detail: "Every milestone disbursement generates a declaration to the Ministry of Finance, reviewed and filed by oversight." },
  { rule: "Single-spend threshold", detail: "Any single spend at or above 400M UGX requires a compliance declaration before approval." },
  { rule: "Counterparty verification", detail: "Contractors and subcontractors carry KYC; government and funder entities carry a lighter verification record." },
];

export function ComplianceView() {
  const seed = useSeed();
  const declarations = useDeclarations();
  const money = useMoney();
  const { file: fileDecl, isPending: filing } = useFileDeclaration();
  const people = usePeopleIndex();
  const [open, setOpen] = useState<Declaration | null>(null);

  const drafts = declarations.filter((d) => d.status === "draft").length;

  const columns: Column<Declaration>[] = [
    {
      key: "title",
      header: "Declaration",
      cell: (d) => (
        <div>
          <p className="font-medium text-ink">{d.title}</p>
          <p className="text-xs text-faint">{d.receivingEntity}</p>
        </div>
      ),
    },
    { key: "amount", header: "Amount", align: "right", cell: (d) => <span className="tabular-nums text-ink">{money(d.amount)}</span> },
    { key: "authority", header: "Authority", cell: (d) => <span className="text-mute">{d.authority}</span> },
    { key: "date", header: "Date", cell: (d) => <span className="data text-mute">{formatCardDate(d.date)}</span> },
    {
      key: "status",
      header: "Status",
      cell: (d) => {
        const daysSince = Math.round((Date.now() - new Date(d.date).getTime()) / 86_400_000);
        return (
          <div>
            <Pill variant={d.status === "filed" ? "success" : "warn"}>{d.status === "filed" ? "Filed" : "Draft"}</Pill>
            {d.status === "draft" && daysSince > 0 && (
              <p className={`mt-0.5 text-[11px] tabular-nums ${daysSince > 7 ? "text-bad" : "text-warn"}`}>{daysSince}d outstanding</p>
            )}
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (d) => <Button size="sm" variant="secondary" onClick={() => setOpen(d)}>View</Button>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Pack summary */}
      <section>
        <div className="mb-2 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-brand" />
          <h2 className="text-sm font-semibold text-ink">{seed.workspace.compliancePackName}</h2>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {PACK_RULES.map((r) => (
            <Card key={r.rule}>
              <CardBody>
                <p className="text-sm font-medium text-ink">{r.rule}</p>
                <p className="mt-1 text-xs leading-snug text-mute">{r.detail}</p>
              </CardBody>
            </Card>
          ))}
        </div>
      </section>

      {/* Declarations register */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink"><FileCheck2 className="h-4 w-4 text-brand" /> Declarations register</h2>
        </div>
        {drafts > 0 && (
          <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-warn/30 bg-warnSoft px-4 py-2.5">
            <p className="text-sm font-medium text-warn">
              <span className="tabular-nums">{drafts}</span> draft declaration{drafts > 1 ? "s" : ""} awaiting filing with the authority
            </p>
            <button onClick={() => setOpen(declarations.find((d) => d.status === "draft") ?? null)} className="shrink-0 rounded-md border border-warn/30 px-2.5 py-1 text-xs font-medium text-warn hover:bg-warn/10">
              File now
            </button>
          </div>
        )}
        <NarrativeCard
          variant="briefing"
          title="The platform does the regulatory paperwork"
          body="The deal's compliance pack defines what must be declared; the ledger supplies the data; oversight reviews and files. Each milestone disbursement generates an inbound-funds declaration automatically."
        />
        <div className="mt-3">
          <DataTable
            columns={columns}
            rows={declarations}
            rowKey={(d) => d.id}
            onRowClick={setOpen}
            empty={<EmptyState icon={<FileText className="h-5 w-5" />} title="No declarations yet" body="When a milestone disbursement is approved, an inbound-funds declaration is generated here for review and filing." />}
          />
        </div>
      </section>

      <DeclarationDrawer
        declaration={open}
        onClose={() => setOpen(null)}
        programmeName={seed.programme.name}
        filedByName={open?.filedById ? people[open.filedById]?.fullName : undefined}
        onFile={async () => { if (open) { await fileDecl(open.id); toast.success(`Filed with the ${open.authority}`); setOpen(null); } }}
        filing={filing}
        money={money}
      />
    </div>
  );
}

function DeclarationDrawer({
  declaration,
  onClose,
  programmeName,
  filedByName,
  onFile,
  filing,
  money,
}: {
  declaration: Declaration | null;
  onClose: () => void;
  programmeName: string;
  filedByName?: string;
  onFile: () => void;
  filing?: boolean;
  money: (n: number) => string;
}) {
  if (!declaration) return null;
  const d = declaration;
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} width="620px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">{d.title}</DrawerTitle>
        <p className="mt-0.5 text-xs text-faint">{d.status === "filed" ? "Filed" : "Draft"} · {d.authority}</p>
      </DrawerHeader>
      <DrawerBody>
        {/* Printable document view */}
        <div className="rounded-lg border border-line bg-card p-5 text-sm">
          <p className="text-center text-xs uppercase tracking-widest text-faint">Inbound funds declaration</p>
          <p className="mt-3 text-ink"><span className="text-faint">To:</span> {d.authority}</p>
          <p className="text-ink"><span className="text-faint">Programme:</span> {programmeName}</p>
          <div className="my-4 border-t border-hairline" />
          <dl className="space-y-2">
            <Row label="Receiving entity" value={d.receivingEntity} />
            <Row label="Purpose" value={d.purpose} />
            <Row label="Amount (equivalent inbound)" value={money(d.amount)} />
            <Row label="Date" value={format(new Date(d.date), "d MMMM yyyy")} mono />
            <Row label="Reference" value={d.disbursementId ?? d.id} mono />
          </dl>
          <div className="my-4 border-t border-hairline" />
          <p className="text-xs text-mute">
            {d.status === "filed"
              ? `Filed${filedByName ? ` by ${filedByName}` : ""}${d.filedAt ? ` on ${formatCardDate(d.filedAt)}` : ""}.`
              : "Generated from the disbursement record. Review the figures, then file with the authority."}
          </p>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Button variant="ghost" onClick={() => toast.message("PDF export is mocked in this prototype.")}><Download className="h-4 w-4" /> Download PDF</Button>
        {d.status === "draft" && <Button disabled={filing} onClick={onFile}>File with {d.authority}</Button>}
      </DrawerFooter>
    </Drawer>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-faint">{label}</dt>
      <dd className={`text-right font-medium text-ink${mono ? " data" : ""}`}>{value}</dd>
    </div>
  );
}
